import { UseQueryResult } from "@tanstack/react-query";
import { ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";

import { InvoiceEntityTableToolbar } from "./InvoiceEntityTableToolbar";
import { useBulkUpdateCampaignJobBoardInvoices } from "../../hooks/useBulkUpdateCampaignJobBoardInvoices";
import { useBulkUpdateChannelInvoices } from "../../hooks/useBulkUpdateChannelInvoices";
import { useDownloadInvoiceCsv } from "../../hooks/useDownloadInvoiceCsv";

import ModernPageLayout from "@/components/layout/ModernPageLayout";
import { DataTable } from "@/components/ui/v2/table/data-table";
import {
  EditableDataTable,
  EditableDataTableRef,
} from "@/components/ui/v2/table/editable-data-table";
import Tabs from "@/components/ui/v2/tabs";
import { ROUTES } from "@/constants/routes";
import { useAuth } from "@/lib/hooks/useAuth";
import { useBreadcrumbs } from "@/lib/hooks/useBreadcrumbs";
import { useDateConstraints } from "@/lib/hooks/useDateConstraints";
import { usePageSearchParams } from "@/lib/hooks/usePageSearchParams";
import { usePageSizeSearchParams } from "@/lib/hooks/usePageSizeSearchParams";
import { useSearchSearchParams } from "@/lib/hooks/useSearchSearchParams";
import {
  useSortSearchParams,
  useTableSortHandler,
} from "@/lib/hooks/useSortSearchParams";
import { isYearMonthInFuture } from "@/lib/utils/dateUtils";
import { PagedResponse } from "@/types/response/PagedResponse";
import { TableColumn } from "@/types/table/tableProps";

function getTabs(
  t: (key: string) => string,
  routeSegment: string,
  entityId: string | undefined,
  year: string | undefined,
  month: string | undefined,
) {
  if (!entityId || !year || !month) {
    return [];
  }

  return [
    {
      id: "toDo",
      label: t("common.toDo"),
      href: `/${ROUTES.invoices}/${routeSegment}/${entityId}/${ROUTES.toDo}/${year}/${month}`,
      content: <></>,
    },
    {
      id: "invoiced",
      label: t("common.invoiced"),
      href: `/${ROUTES.invoices}/${routeSegment}/${entityId}/${ROUTES.invoiced}/${year}/${month}`,
      content: <></>,
    },
  ];
}

type Breadcrumb = {
  label: string;
  listLabel: string;
};

type InvoicedEntityPageConfig<TInvoice, TEntity> = {
  entityIdParam: string;
  routeSegment: string;
  apiEndpointBase: string;
  // Using any here because hooks have specific param types (channelId, jobBoardId, etc.)
  // that can't be statically verified due to dynamic entityIdParam key.
  // The component creates params with [entityIdParam]: entityId which matches
  // the hook's expected property name at runtime, but TypeScript can't verify this.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  useInvoices: (params: any) => UseQueryResult<PagedResponse<TInvoice>>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  useEntity: (params: any) => UseQueryResult<TEntity>;
  useTableColumns: () => {
    [key: string]: TableColumn<TInvoice>[] | ReactNode;
    dialog?: ReactNode;
  };
  getColumnsFromResult: (
    result: ReturnType<
      InvoicedEntityPageConfig<TInvoice, TEntity>["useTableColumns"]
    >,
  ) => TableColumn<TInvoice>[];
  getRowId: (row: TInvoice) => string;
  locationStateKey: string;
  breadcrumb: Breadcrumb;
  entityNameKey: keyof TEntity;
  // Function to map invoice data to bulk create request format
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mapInvoiceToRequest: (invoice: TInvoice) => any;
  // Field name that should be editable (e.g., "amount")
  editableField: keyof TInvoice;
};

export function InvoicedEntityPage<
  TInvoice,
  TEntity extends { name?: string },
>({
  entityIdParam,
  routeSegment,
  apiEndpointBase,
  useInvoices,
  useEntity,
  useTableColumns,
  getColumnsFromResult,
  getRowId,
  locationStateKey,
  breadcrumb,
  entityNameKey,
  mapInvoiceToRequest,
  editableField,
}: InvoicedEntityPageConfig<TInvoice, TEntity>) {
  const params = useParams();
  const entityId = params[entityIdParam];
  const { year, month } = useParams();
  const { orderBy, descending } = useSortSearchParams();
  const { page, setPage } = usePageSearchParams();
  const {
    searchValue,
    debouncedSearchValue,
    setSearchValue,
  } = useSearchSearchParams();
  const { pageSize, setPageSize } = usePageSizeSearchParams();
  const { tenantId } = useAuth();
  const location = useLocation();
  const handleSort = useTableSortHandler<TInvoice>();
  const { t } = useTranslation();
  const { maxDate } = useDateConstraints();
  const [isEditMode, setIsEditMode] = useState(false);
  const editableTableRef = useRef<EditableDataTableRef<TInvoice>>(null);

  // Determine which bulk update hook to use based on route segment
  const bulkUpdateJobBoardInvoices = useBulkUpdateCampaignJobBoardInvoices({
    jobBoardId: routeSegment === ROUTES.jobBoards ? entityId! : "",
    year: year!,
    month: month!,
    tenantId,
  });

  const bulkUpdateChannelInvoices = useBulkUpdateChannelInvoices({
    channelId: routeSegment === ROUTES.channels ? entityId! : "",
    year: year!,
    month: month!,
    tenantId,
  });

  const bulkUpdateMutation =
    routeSegment === ROUTES.jobBoards
      ? bulkUpdateJobBoardInvoices
      : bulkUpdateChannelInvoices;

  const {
    data: invoices,
    isFetching,
    isLoading,
  } = useInvoices({
    [entityIdParam]: entityId!,
    year: year!,
    month: month!,
    tenantId,
    descending,
    orderBy,
    page,
    pageSize,
    search: debouncedSearchValue,
  });

  const { data: entity } = useEntity({
    [entityIdParam]: entityId!,
    tenantId,
  });

  const tableColumnsResult = useTableColumns();
  const columns = getColumnsFromResult(tableColumnsResult);
  const dialog = tableColumnsResult.dialog;

  const downloadMutation = useDownloadInvoiceCsv();

  // Reset edit mode when pagination/search/month/year changes
  useEffect(() => {
    if (isEditMode) {
      setIsEditMode(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, searchValue, year, month]);

  // Auto-navigate to previous page if current page becomes empty after invoice update/creation
  useEffect(() => {
    if (
      !isLoading &&
      !isFetching &&
      invoices?.data &&
      invoices.data.length === 0 &&
      page > 1 &&
      invoices.totalRecords !== undefined
    ) {
      // Calculate the last page with data
      const totalPages = Math.ceil(invoices.totalRecords / pageSize);
      const targetPage = Math.max(1, totalPages);
      if (targetPage !== page) {
        setPage(targetPage);
      }
    }
  }, [
    invoices?.data,
    invoices?.totalRecords,
    isLoading,
    isFetching,
    page,
    pageSize,
    setPage,
  ]);

  const handleUpdateData = async (newData: TInvoice[]) => {
    const invoicesToUpdate = newData
      .filter((item) => {
        const value = item[editableField];
        return value !== null && value !== undefined;
      })
      .map((item) => mapInvoiceToRequest(item));

    if (invoicesToUpdate.length === 0) return;

    // Use bulk update endpoint
    await bulkUpdateMutation.mutateAsync({
      invoices: invoicesToUpdate,
    });
  };

  const handleSaveChanges = async () => {
    const editedData = editableTableRef.current?.getEditedData() ?? [];
    await handleUpdateData(editedData);
    setIsEditMode(false);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
  };

  const handleEditModeChange = (newEditMode: boolean) => {
    setIsEditMode(newEditMode);
  };

  const handleDownloadCsv = () => {
    if (!entityId || !year || !month) return;
    downloadMutation.mutate({
      apiEndpointBase,
      entityId,
      year,
      month,
      tenantId,
    });
  };

  const getSelectedTabId = () => {
    const currentPath = location.pathname;
    if (currentPath.includes(`/${ROUTES.toDo}`)) {
      return "toDo";
    }
    if (currentPath.includes(`/${ROUTES.invoiced}`)) {
      return "invoiced";
    }
    return "invoiced";
  };

  const tabs = getTabs(t, routeSegment, entityId, year, month);

  const entityName =
    (location.state as Record<string, string> | null)?.[locationStateKey] ??
    (entity?.[entityNameKey] as string | undefined);

  const breadcrumbs = useBreadcrumbs({
    items: [
      { label: t("common.invoices"), href: `/${ROUTES.invoices}` },
      {
        label: breadcrumb.listLabel,
        href: `/${ROUTES.invoices}/${routeSegment}`,
      },
      {
        label: (entityName as string) ?? breadcrumb.label,
      },
    ],
  });

  const currentPageLabel = entityName as string | undefined;

  if (!entityId || !month || !year) return null;

  if (!columns || columns.length === 0) {
    return null;
  }

  return (
    <ModernPageLayout
      backbutton={{
        href: `../../`,
        label: t("common.back"),
      }}
      breadcrumbs={breadcrumbs}
      title={`${t("common.invoices")} ${t("common.for")} ${
        currentPageLabel ?? "..."
      }`}
    >
      <div className="flex flex-col gap-8">
        <div className="flex items-center justify-between w-full">
          <Tabs tabs={tabs} defaultTabId={getSelectedTabId()} />
        </div>

        <InvoiceEntityTableToolbar
          onSearchChange={setSearchValue}
          onDownload={handleDownloadCsv}
          searchValue={searchValue}
          year={year}
          month={month}
          entityId={entityId}
          routeSegment={routeSegment}
          locationStateKey={locationStateKey}
          maxDate={maxDate}
          currentPageLabel={currentPageLabel}
          entityName={entity?.[entityNameKey] as string | undefined}
          isEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSave={handleSaveChanges}
          onCancel={handleCancelEdit}
          isEditDisabled={isYearMonthInFuture([year!, month!])}
          isSaving={bulkUpdateMutation.isPending}
          hasData={(invoices?.data?.length ?? 0) > 0}
          isDownloading={downloadMutation.isPending}
        />

        {isEditMode ? (
          <EditableDataTable<TInvoice>
            ref={editableTableRef}
            columns={columns}
            data={invoices?.data ?? []}
            descending={descending}
            editableFields={[editableField]}
            entityName={"Invoice"}
            getRowId={getRowId}
            isEditDisabled={isYearMonthInFuture([year!, month!])}
            isEditing={bulkUpdateMutation.isPending}
            isEditMode={isEditMode}
            onEditModeChange={handleEditModeChange}
            isLoading={isLoading || isFetching || bulkUpdateMutation.isPending}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            onSort={handleSort}
            onUpdateData={handleUpdateData}
            onSave={handleSaveChanges}
            onCancel={handleCancelEdit}
            orderBy={orderBy}
            page={page}
            pageSize={pageSize}
            totalRecords={invoices?.totalRecords ?? 0}
          />
        ) : (
          <DataTable
            columns={columns}
            data={invoices?.data}
            isLoading={isLoading || isFetching}
            orderBy={orderBy}
            descending={descending}
            onSort={handleSort}
            page={page}
            pageSize={pageSize}
            totalRecords={invoices?.totalRecords ?? 0}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            getRowId={getRowId}
          />
        )}
        {dialog}
      </div>
    </ModernPageLayout>
  );
}
